<template>
    <VueDropzone
        :id="id"
        ref="el"
        :options="dropzoneOptions"
        @vdropzone-error="onError"
        @vdropzone-complete="onComplete"
        @vdropzone-queue-complete="onQueueComplete"
        @vdropzone-files-added="handleFilesAdded"
    >
        <slot></slot>
    </VueDropzone>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, watch, computed, type PropType } from 'vue';
import VueDropzone from 'dropzone-vue3';
import type { DropzoneFile, DropzoneOptions } from 'dropzone-vue3';
import { captureException } from '@sentry/vue';
// eslint-disable-next-line import/no-restricted-paths
import { useBackendHeaders } from '@/api/backend';
import authTokenUtils from '@/utils/authTokenUtils';
import { useId } from '@/composables/id';
import { timeout } from '@/helpers/misc';

/**
 * This component customizes the dropzone-vue3 component to work properly with our api integration & automatic token refreshes.
 * You can invoke function on the underlying dropzone component via the `call` method, like call(<functionName>, <param1>, <param2>)
 */
export default defineComponent({
    name: 'Dropzone',
    components: {
        VueDropzone,
    },
    props: {
        /**
         * The options to pass to the dropzone-vue3 component.
         * @see https://www.dropzone.dev/
         */
        options: {
            type: Object as PropType<DropzoneOptions>,
            default: () => ({}),
        },
    },
    emits: [
        'vdropzone-error',
        'vdropzone-complete',
        'vdropzone-queue-complete',
        'vdropzone-files-added',
    ],
    setup(props, { emit, attrs }) {
        const el = ref();
        const id = attrs.id ?? `dropzone-${useId()}`;

        const headers = reactive(useBackendHeaders());
        watch(
            headers,
            (val) => {
                el.value?.setOption('headers', val);
            },
            { deep: true },
        );

        const dropzoneOptions = computed(() => ({
            hiddenInputContainer: `#${id}`,
            ...props.options,
            headers,
        }));

        /* eslint-disable no-param-reassign */
        const requeueFile = (file: DropzoneFile) => {
            file.status = 'queued';
            file.xhr = undefined;
            if (file.upload) {
                file.upload.progress = 0;
                file.upload.bytesSent = 0;
            }
        };
        /* eslint-enable no-param-reassign */

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function call(func: string, ...args: any[]): any {
            return el.value?.[func]?.(...args);
        }

        const onError: DropzoneOptions['error'] = async (file, message, xhr) => {
            if (xhr && authTokenUtils.shouldRefreshAuthTokenFor(xhr.status, xhr.responseURL)) {
                try {
                    // try a token refresh
                    if (await authTokenUtils.refreshAuthToken()) {
                        requeueFile(file);
                        setTimeout(() => {
                            el.value?.processQueue();
                        }, 0);
                        return;
                    }
                } catch (e) {
                    // eslint-disable-next-line
                    console.error(e);
                    captureException(e);
                    emit('vdropzone-complete', file);
                }
            }

            // if token refresh couldn't be handled, forward the error further
            emit('vdropzone-error', file, message, xhr);
        };

        const onComplete: DropzoneOptions['complete'] = (file) => {
            // don't propagate token refresh errors
            // they will be handled by the onError function
            if (file.xhr && authTokenUtils.shouldRefreshAuthTokenFor(file.xhr.status, file.xhr.responseURL)) return;

            emit('vdropzone-complete', file);
        };

        const onQueueComplete: DropzoneOptions['queuecomplete'] = async () => {
            // the package also listens for this event, so we need to wait for the next tick to run our logic afterwards
            await timeout(0);

            // when calling `processQueue` manually, the package will reset the `autoProcessQueue` option, so we restore it.
            call('setOption', 'autoProcessQueue', el.value.wasQueueAutoProcess);
            emit('vdropzone-queue-complete');
        };

        const handleFilesAdded: DropzoneOptions['addedfiles'] = (files) => {
            emit('vdropzone-files-added', files);
        };
        return {
            el,
            id,
            dropzoneOptions,
            onError,
            onComplete,
            onQueueComplete,
            handleFilesAdded,
            call,
            invoke: () => {
                // @ts-ignore $el is an internal vue attribute and not part of the Dropzone type
                el.value?.$el?.click?.();
            },
        };
    },
});
</script>

<style lang="scss" scoped></style>
